import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Info, X, XCircle } from 'lucide-react';

const ToastContext = createContext(() => {});

/** `const toast = useToast(); toast({ title, description, variant })` */
export const useToast = () => useContext(ToastContext);

const icons = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
};

const accents = {
  success: 'text-brand-600',
  error: 'text-red-600',
  info: 'text-blue-600',
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback(
    (id) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    []
  );

  const toast = useCallback(
    ({ title, description, variant = 'success', duration = 5000 }) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, title, description, variant }]);
      setTimeout(() => dismiss(id), duration);
    },
    [dismiss]
  );

  const value = useMemo(() => toast, [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          aria-atomic="false"
          className="pointer-events-none fixed bottom-5 right-4 z-[80] flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-3 sm:right-6"
        >
          <AnimatePresence initial={false}>
            {toasts.map(({ id, title, description, variant }) => {
              const IconTag = icons[variant] || Info;
              return (
                <motion.div
                  key={id}
                  layout
                  initial={{ opacity: 0, y: 20, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 40, scale: 0.96 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="pointer-events-auto flex items-start gap-3 rounded-xl2 border border-ink-100 bg-white p-4 shadow-lift"
                >
                  <IconTag className={`h-5 w-5 flex-none ${accents[variant]}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ink-900">{title}</p>
                    {description && (
                      <p className="mt-0.5 text-xs leading-relaxed text-ink-500">{description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => dismiss(id)}
                    aria-label="Dismiss notification"
                    className="-mr-1 -mt-1 grid h-7 w-7 flex-none place-items-center rounded-full text-ink-400 transition hover:bg-ink-100 hover:text-ink-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export default ToastProvider;
